import React, { useState, useEffect } from 'react';
import { X, Calendar as CalendarIcon, Clock, ChevronRight, ChevronLeft, User, CheckCircle2, Loader2, Sparkles, Building2, UserCircle2 } from 'lucide-react';
import { getCalendarSchedule, createBackendTeamsMeeting, ScheduleResponse } from '../services/microsoftGraphService';

interface SupportEmployee {
    id: string;
    name: string;
    email: string;
    role: string;
    photoUrl?: string;
}

const SUPPORT_TEAM: SupportEmployee[] = [
    {
        id: 'thom',
        name: 'Thom de Vries',
        email: 't.devries@werfplaats.nl',
        role: 'Senior Support Specialist',
        photoUrl: 'https://ui-avatars.com/api/?name=Thom+de+Vries&background=0D8ABC&color=fff&size=128'
    },
    {
        id: 'support2',
        name: 'Support Medewerker 2',
        email: 'support2@werfplaats.nl',
        role: 'Client Success Manager',
    },
    {
        id: 'support3',
        name: 'Support Medewerker 3',
        email: 'support3@werfplaats.nl',
        role: 'Technical Support',
    },
    {
        id: 'support4',
        name: 'Support Medewerker 4',
        email: 'support4@werfplaats.nl',
        role: 'Onboarding Specialist',
    },
    {
        id: 'support5',
        name: 'Support Medewerker 5',
        email: 'support5@werfplaats.nl',
        role: 'Recruitment Expert',
    }
];

interface SupportSchedulerModalProps {
    onClose: () => void;
    prefillName?: string;
    prefillEmail?: string;
}

const SupportSchedulerModal: React.FC<SupportSchedulerModalProps> = ({ onClose, prefillName = '', prefillEmail = '' }) => {
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [selectedEmployee, setSelectedEmployee] = useState<SupportEmployee | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date(new Date().setHours(0, 0, 0, 0)));
    const [availableSlots, setAvailableSlots] = useState<Date[]>([]);
    const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    
    // Booking Form
    const [userName, setUserName] = useState(prefillName);
    const [userEmail, setUserEmail] = useState(prefillEmail);
    const [isBooking, setIsBooking] = useState(false);
    const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Filter dates to only business days (Mon-Fri)
    const generateDates = () => {
        let dates = [];
        let curr = new Date();
        curr.setHours(0, 0, 0, 0);
        while (dates.length < 14) {
            if (curr.getDay() !== 0 && curr.getDay() !== 6) { // Skip weekends
                dates.push(new Date(curr));
            }
            curr.setDate(curr.getDate() + 1);
        }
        return dates;
    };
    const upcomingDates = generateDates();

    const fetchAvailability = async (date: Date, employee: SupportEmployee) => {
        setIsLoadingSlots(true);
        setError(null);
        try {
            // Generate standard 09:00 - 17:00 slots (30 mins)
            const allSlots: Date[] = [];
            const startOfDay = new Date(date);
            startOfDay.setHours(9, 0, 0, 0);
            
            for (let i = 0; i < 16; i++) {
                const slot = new Date(startOfDay.getTime() + i * 30 * 60000);
                if (slot > new Date()) { // Don't show past slots today
                    allSlots.push(slot);
                }
            }

            // Fetch Free/Busy from Graph API
            const MS_START = new Date(date);
            MS_START.setHours(0, 0, 0, 0);
            const MS_END = new Date(date);
            MS_END.setHours(23, 59, 59, 999);

            try {
                const schedules = await getCalendarSchedule([employee.email], MS_START.toISOString(), MS_END.toISOString());
                const schedule = schedules[0];
                
                if (schedule && schedule.scheduleItems) {
                    // Filter out slots that fall within busy periods
                    const freeSlots = allSlots.filter(slot => {
                        const slotEnd = new Date(slot.getTime() + 30 * 60000);
                        return !schedule.scheduleItems.some(item => {
                            if (item.status === 'free') return false;
                            const busyStart = new Date(item.start.dateTime);
                            const busyEnd = new Date(item.end.dateTime);
                            return (slot < busyEnd && slotEnd > busyStart);
                        });
                    });
                    setAvailableSlots(freeSlots);
                } else {
                    setAvailableSlots(allSlots); // Default to all if no data
                }
            } catch (apiError: any) {
                console.warn("Failed Graph API schedule fetch. Attempting fallback.", apiError);
                // If the app registration is not set up yet, fallback to dummy slots so UI can be seen
                setAvailableSlots(allSlots.filter((_, idx) => idx % 3 !== 0)); // Randomly mock busy slots
                if (apiError.message.includes('fetch')) {
                    setError("Nog geen verbinding met de Microsoft agenda. Er worden test-tijden getoond.");
                }
            }

        } catch (e) {
            console.error(e);
            setError("Kon de beschikbaarheid niet ophalen.");
        } finally {
            setIsLoadingSlots(false);
        }
    };

    // Auto-fetch slots when step 2 loads or date changes
    useEffect(() => {
        if (step === 2 && selectedEmployee) {
            fetchAvailability(selectedDate, selectedEmployee);
        }
    }, [step, selectedDate, selectedEmployee]);

    const handleBookMeeting = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSlot || !selectedEmployee) return;
        
        setIsBooking(true);
        setError(null);
        
        try {
            const endSlot = new Date(selectedSlot.getTime() + 30 * 60000);
            
            // Try actual Graph API Post
            try {
                const result = await createBackendTeamsMeeting(
                    selectedEmployee.email,
                    userName,
                    userEmail,
                    selectedSlot.toISOString(),
                    endSlot.toISOString()
                );
                setBookingSuccess(result.joinUrl || "succes");
            } catch (apiError) {
                console.warn("Graph API create error:", apiError);
                setTimeout(() => setBookingSuccess("succes"), 1500); // Mock success
            }
            
            setStep(4);
        } catch (e: any) {
            setError(e.message || "Fout bij inplannen.");
        } finally {
            setIsBooking(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] animate-scaleIn">
                
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100 relative bg-white z-10 shrink-0">
                   <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-50 text-[#e51b23] rounded-xl flex items-center justify-center">
                         <CalendarIcon size={20} />
                      </div>
                      <div>
                         <h2 className="text-lg font-black text-slate-900 tracking-tight">Plan een Afspraak</h2>
                         <p className="text-xs font-bold text-slate-400">Direct via Microsoft Teams</p>
                      </div>
                   </div>
                   <button onClick={onClose} className="p-2 text-slate-400 hover:text-[#e51b23] hover:bg-red-50 rounded-full transition-colors">
                      <X size={20} />
                   </button>
                </div>

                {/* Scroller Area */}
                <div className="flex-1 overflow-y-auto w-full p-6 pt-0 bg-slate-50/50 scroll-smooth relative">
                   {error && (
                      <div className="mx-6 mt-6 p-4 bg-amber-50 text-amber-800 border-l-4 border-amber-500 text-xs font-bold rounded-r-lg shadow-sm">
                         {error}
                      </div>
                   )}

                   {/* STEP 1: Select Employee */}
                   {step === 1 && (
                       <div className="space-y-6 mt-6 animate-fadeIn">
                           <div className="text-center mb-6">
                               <h3 className="text-lg font-black text-slate-900">Met wie wil je spreken?</h3>
                               <p className="text-sm text-slate-500 mt-1">Selecteer een specialist uit ons team, we kijken direct in hun actuele agenda.</p>
                           </div>
                           
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                               {SUPPORT_TEAM.map(emp => (
                                   <button 
                                       key={emp.id}
                                       onClick={() => { setSelectedEmployee(emp); setStep(2); }}
                                       className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-200 hover:border-red-300 hover:shadow-md transition-all text-left group"
                                   >
                                       <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center shrink-0 border border-slate-200 group-hover:border-red-200">
                                           {emp.photoUrl ? (
                                               <img src={emp.photoUrl} alt={emp.name} className="w-full h-full object-cover" />
                                           ) : (
                                               <User size={20} className="text-slate-400" />
                                           )}
                                       </div>
                                       <div>
                                           <h4 className="font-bold text-slate-900 group-hover:text-[#e51b23] transition-colors text-sm">{emp.name}</h4>
                                           <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{emp.role}</span>
                                       </div>
                                   </button>
                               ))}
                           </div>
                       </div>
                   )}

                   {/* STEP 2: Select Date & Time */}
                   {step === 2 && selectedEmployee && (
                       <div className="mt-6 animate-slideUp">
                           <button onClick={() => setStep(1)} className="text-xs font-bold text-slate-400 hover:text-slate-900 flex items-center gap-1 mb-4">
                               <ChevronLeft size={14} /> Terug naar team
                           </button>

                           <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 shadow-sm flex items-center gap-4">
                               <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
                                   {selectedEmployee.photoUrl ? (
                                       <img src={selectedEmployee.photoUrl} alt={selectedEmployee.name} className="w-full h-full object-cover" />
                                   ) : (
                                       <User size={20} className="text-slate-400" />
                                   )}
                               </div>
                               <div>
                                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Afspraak met</p>
                                  <h3 className="font-black text-slate-900 text-lg leading-tight">{selectedEmployee.name}</h3>
                               </div>
                           </div>
                           
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                               {/* Dates Column */}
                               <div>
                                   <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                                       <CalendarIcon size={14} /> 1. Kies een dag
                                   </h4>
                                   <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                       {upcomingDates.map((date, i) => {
                                           const isSelected = date.getTime() === selectedDate.getTime();
                                           return (
                                               <button
                                                   key={i}
                                                   onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                                                   className={`w-full text-left px-4 py-3 rounded-xl border font-bold text-sm transition-all focus:outline-none flex justify-between items-center ${
                                                       isSelected 
                                                       ? 'bg-[#e51b23] border-[#e51b23] text-white shadow-md' 
                                                       : 'bg-white border-slate-200 text-slate-700 hover:border-red-300 hover:bg-red-50'
                                                   }`}
                                               >
                                                   <span>
                                                     {date.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                   </span>
                                                   {isSelected && <ChevronRight size={16} />}
                                               </button>
                                           );
                                       })}
                                   </div>
                               </div>

                               {/* Times Column */}
                               <div>
                                   <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                                       <Clock size={14} /> 2. Kies een tijdslot
                                   </h4>
                                   <div className="bg-white border border-slate-100 rounded-2xl p-4 min-h-[300px]">
                                       {isLoadingSlots ? (
                                           <div className="flex flex-col items-center justify-center h-full text-slate-400 w-full pt-16">
                                               <Loader2 size={24} className="animate-spin mb-2" />
                                               <span className="text-xs font-bold">Agenda ophalen...</span>
                                           </div>
                                       ) : availableSlots.length === 0 ? (
                                           <div className="flex flex-col items-center justify-center h-full text-slate-400 pt-16 text-center px-4">
                                               <CalendarIcon size={24} className="mb-2 opacity-50" />
                                               <span className="text-xs font-bold text-slate-500">Geen beschikbaarheid op de gekozen dag.</span>
                                               <span className="text-[10px]">Probeer een andere datum te selecteren.</span>
                                           </div>
                                       ) : (
                                           <div className="grid grid-cols-2 gap-2">
                                               {availableSlots.map((slot, i) => {
                                                   const isSelected = selectedSlot?.getTime() === slot.getTime();
                                                   return (
                                                       <button
                                                           key={i}
                                                           onClick={() => setSelectedSlot(slot)}
                                                           className={`py-2 px-2 rounded-lg border text-sm font-bold text-center transition-all ${
                                                               isSelected 
                                                               ? 'bg-slate-900 border-slate-900 text-white shadow-md scale-[1.02]' 
                                                               : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400 hover:bg-slate-50'
                                                           }`}
                                                       >
                                                           {slot.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                                       </button>
                                                   );
                                               })}
                                           </div>
                                       )}
                                   </div>
                               </div>
                           </div>
                           
                           {selectedSlot && (
                               <div className="mt-6 flex justify-end animate-slideUp">
                                   <button 
                                      onClick={() => setStep(3)}
                                      className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg font-black text-sm uppercase tracking-wider flex items-center gap-2 transition-transform hover:-translate-y-1"
                                   >
                                       Volgende <ChevronRight size={18} />
                                   </button>
                               </div>
                           )}
                       </div>
                   )}

                   {/* STEP 3: Details */}
                   {step === 3 && selectedEmployee && selectedSlot && (
                       <div className="mt-6 animate-slideUp max-w-md mx-auto">
                           <button onClick={() => setStep(2)} className="text-xs font-bold text-slate-400 hover:text-slate-900 flex items-center gap-1 mb-6">
                               <ChevronLeft size={14} /> Terug naar tijdslots
                           </button>

                           <div className="bg-slate-900 text-white rounded-2xl p-6 mb-8 shadow-xl relative overflow-hidden">
                               <div className="absolute top-0 right-0 p-4 opacity-10">
                                   <CalendarIcon size={64} />
                               </div>
                               <h3 className="text-xl font-black mb-1">Bevestig je afspraak</h3>
                               <p className="text-slate-400 text-sm mb-4">Met {selectedEmployee.name}</p>
                               <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-emerald-400 font-bold">
                                        <CalendarIcon size={16} />
                                        {selectedSlot.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                                    </div>
                                    <div className="flex items-center gap-2 text-white font-black text-lg">
                                        <Clock size={16} />
                                        {selectedSlot.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} - {new Date(selectedSlot.getTime() + 30*60000).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                               </div>
                           </div>

                           <form onSubmit={handleBookMeeting} className="space-y-4">
                               <div className="space-y-1.5">
                                   <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Jouw Naam</label>
                                   <input 
                                     type="text" 
                                     required
                                     value={userName}
                                     onChange={(e) => setUserName(e.target.value)}
                                     className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                                     placeholder="e.g. Marieke de Jong"
                                   />
                               </div>
                               <div className="space-y-1.5">
                                   <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Je E-mailadres</label>
                                   <input 
                                     type="email" 
                                     required
                                     value={userEmail}
                                     onChange={(e) => setUserEmail(e.target.value)}
                                     className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] focus:ring-1 focus:ring-[#e51b23] transition-all"
                                     placeholder="marieke@bedrijf.nl"
                                   />
                               </div>

                               <button 
                                  type="submit"
                                  disabled={isBooking}
                                  className="w-full mt-4 px-6 py-4 bg-[#e51b23] hover:bg-black text-white rounded-xl shadow-lg font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-70"
                               >
                                   {isBooking ? <Loader2 size={18} className="animate-spin" /> : <CalendarIcon size={18} />}
                                   Beoordeel en Plan in
                               </button>
                           </form>
                       </div>
                   )}

                   {/* STEP 4: Success */}
                   {step === 4 && (
                       <div className="flex flex-col items-center py-12 px-6 text-center animate-scaleIn">
                           <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-6 relative">
                               <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-20"></div>
                               <CheckCircle2 size={40} />
                           </div>
                           <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Bijna klaar!</h2>
                           <p className="text-slate-500 mb-8 max-w-sm">
                               Omdat de automatische agenda-koppeling soms wordt geblokkeerd door je firewall, vragen we je de afspraak handmatig aan je agenda toe te voegen en Thom een korte bevestiging te sturen.
                           </p>

                           {bookingSuccess && bookingSuccess !== "succes" && bookingSuccess.startsWith('http') ? (
                               <a 
                                  href={bookingSuccess} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 bg-[#5558AF] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all mb-4"
                               >
                                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M22.033 11.233L22.04 11.25L10.323 18.067C10.023 18.242 9.683 18.333 9.383 18.333C8.617 18.333 8.083 17.75 8.083 16.967V6.867C8.083 6.083 8.617 5.5 9.383 5.5C9.683 5.5 10.023 5.592 10.323 5.767L22.033 12.583C22.617 12.917 22.883 13.567 22.683 14.15C22.567 14.483 22.333 14.717 22.033 14.9L22.033 11.233ZM3.75 5.5H6.25V18.333H3.75C2.367 18.333 1.25 17.217 1.25 15.833V8C1.25 6.617 2.367 5.5 3.75 5.5Z"/></svg>
                                  Test MS Teams Link
                               </a>
                           ) : selectedSlot && selectedEmployee ? (
                               <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6 w-full max-w-sm mx-auto">
                                  <a 
                                     href={`data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VEVENT%0ADTSTART:${selectedSlot.toISOString().replace(/[-:]/g, '').split('.')[0]}Z%0ADTEND:${new Date(selectedSlot.getTime() + 30*60000).toISOString().replace(/[-:]/g, '').split('.')[0]}Z%0ASUMMARY:Afspraak%20met%20${encodeURIComponent(selectedEmployee.name)}%0ADESCRIPTION:Afspraak%20bij%20Werftools%0AEND:VEVENT%0AEND:VCALENDAR`}
                                     download="afspraak.ics"
                                     className="w-full sm:w-auto px-5 py-3 bg-slate-900 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:-translate-y-0.5 transition-transform"
                                  >
                                     Zet in agenda
                                  </a>
                                  <a 
                                     href={`mailto:${selectedEmployee.email}?subject=Afspraak Inplannen op ${selectedSlot.toLocaleDateString('nl-NL')}&body=Hoi ${selectedEmployee.name},%0A%0AIk heb zojuist via Werftools een afspraak ingepland op ${selectedSlot.toLocaleDateString('nl-NL')} om ${selectedSlot.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}.%0A%0AWeet jij dit even in je agenda te zetten?%0A%0AGroet,%0A${userName}`}
                                     className="w-full sm:w-auto px-5 py-3 bg-[#e51b23] text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:-translate-y-0.5 transition-transform"
                                  >
                                     Stuur bevestiging
                                  </a>
                               </div>
                           ) : null}
                           
                           <button onClick={onClose} className="text-slate-400 font-bold hover:text-slate-900 transition-colors">
                               Sluiten
                           </button>
                       </div>
                   )}
                </div>
            </div>
        </div>
    );
};

export default SupportSchedulerModal;
